import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
const supabaseKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseKey) {
  throw new Error('Missing Supabase environment variables')
}

// Initialize Supabase client with optimized configuration for auth issues
export const supabase = createClient(supabaseUrl, supabaseKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    storageKey: 'eventgrid-auth',
    storage: window.localStorage,
    // Add fallback configuration for email confirmation issues
    flowType: 'pkce' // Use PKCE flow for better security and reliability
  },
  db: {
    schema: 'public'
  },
  realtime: {
    params: {
      eventsPerSecond: 10
    }
  },
  // Add global error handling for auth issues
  global: {
    headers: {
      'X-Client-Info': 'eventgrid-web-app'
    }
  }
})

// Enhanced authentication helpers for role-based access including staff
export const authHelpers = {
  // Check if user is authenticated
  isAuthenticated: async () => {
    try {
      const { data: { session } } = await supabase?.auth?.getSession()
      return !!session?.user
    } catch (error) {
      console.error('Error checking authentication:', error)
      return false
    }
  },

  // Get current user with profile
  getCurrentUserWithProfile: async () => {
    try {
      const { data: { user } } = await supabase?.auth?.getUser()
      if (!user) return { user: null, profile: null }

      const { data: profile } = await supabase?.from('user_profiles')?.select('*')?.eq('id', user?.id)?.single()

      return { user, profile }
    } catch (error) {
      console.error('Error getting user with profile:', error)
      return { user: null, profile: null }
    }
  },

  // Enhanced role checking functions with staff support
  hasRole: (profile, role) => profile?.role === role,
  hasAnyRole: (profile, roles) => roles?.includes(profile?.role),
  isHost: (profile) => profile?.role === 'host',
  isAttendee: (profile) => profile?.role === 'attendee',
  isAdmin: (profile) => profile?.role === 'admin',
  isStaff: (staffMember) => !!staffMember && staffMember?.status === 'active',
  isCheckInStaff: (staffMember) => staffMember?.staff_role === 'check_in_staff' && staffMember?.status === 'active',
  
  // Enhanced permission checking for staff
  canCreateEvent: (profile) => profile?.role === 'host' || profile?.role === 'admin',
  canManageEvent: (profile, eventHostId) => {
    return profile?.role === 'admin' || (profile?.role === 'host' && profile?.id === eventHostId)
  },
  canViewEventBookings: (profile, eventHostId) => {
    return profile?.role === 'admin' || (profile?.role === 'host' && profile?.id === eventHostId)
  },
  canManageAllUsers: (profile) => profile?.role === 'admin',
  canManageStaff: (profile) => profile?.role === 'admin' || profile?.role === 'host',
  canCheckInAttendees: (profile, staffMember) => {
    return profile?.role === 'admin' || 
           profile?.role === 'host' || 
           (staffMember && staffMember?.status === 'active')
  }
}

// Database query helpers with built-in permission checking
export const queryHelpers = {
  // Get events based on user role
  getEventsForUser: async (userProfile) => {
    if (!userProfile) {
      // Public users can only see published events
      return await supabase?.from('events')?.select(`
          *,
          host:user_profiles!events_host_id_fkey(full_name, email),
          event_tickets(*)
        `)?.eq('status', 'published')?.order('created_at', { ascending: false });
    }

    if (userProfile?.role === 'admin') {
      // Admins can see all events
      return await supabase?.from('events')?.select(`
          *,
          host:user_profiles!events_host_id_fkey(full_name, email),
          event_tickets(*)
        `)?.order('created_at', { ascending: false });
    }

    if (userProfile?.role === 'host') {
      // Hosts can see their own events and published events
      return await supabase?.from('events')?.select(`
          *,
          host:user_profiles!events_host_id_fkey(full_name, email),
          event_tickets(*)
        `)?.or(`host_id.eq.${userProfile?.id},status.eq.published`)?.order('created_at', { ascending: false });
    }

    // Attendees can only see published events
    return await supabase?.from('events')?.select(`
        *,
        host:user_profiles!events_host_id_fkey(full_name, email),
        event_tickets(*)
      `)?.eq('status', 'published')?.order('created_at', { ascending: false });
  },

  // Get bookings based on user role
  getBookingsForUser: async (userProfile) => {
    if (!userProfile) return { data: [], error: 'Unauthorized' }

    if (userProfile?.role === 'admin') {
      // Admins can see all bookings
      return await supabase?.from('bookings')?.select(`
          *,
          events(*),
          event_tickets(*),
          attendee:user_profiles!bookings_attendee_id_fkey(*)
        `)?.order('created_at', { ascending: false });
    }

    if (userProfile?.role === 'host') {
      // Hosts can see bookings for their events
      return await supabase?.from('bookings')?.select(`
          *,
          events!inner(*),
          event_tickets(*),
          attendee:user_profiles!bookings_attendee_id_fkey(*)
        `)?.eq('events.host_id', userProfile?.id)?.order('created_at', { ascending: false });
    }

    // Attendees can only see their own bookings
    return await supabase?.from('bookings')?.select(`
        *,
        events(*),
        event_tickets(*)
      `)?.eq('attendee_id', userProfile?.id)?.order('created_at', { ascending: false });
  }
}

// Real-time subscription helpers with role-based filtering
export const realtimeHelpers = {
  // Subscribe to events based on user role
  subscribeToEvents: (userProfile, callback) => {
    let filter = {}
    
    if (!userProfile) {
      filter = { filter: 'status=eq.published' }
    } else if (userProfile?.role === 'host') {
      // Hosts see their own events and published events
      filter = { filter: `host_id=eq.${userProfile?.id}` }
    } else if (userProfile?.role === 'attendee') {
      filter = { filter: 'status=eq.published' }
    }
    // Admins see all events (no filter)

    return supabase?.channel('events-channel')?.on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'events',
        ...filter
      }, callback)?.subscribe();
  },

  // Subscribe to bookings based on user role  
  subscribeToBookings: (userProfile, callback) => {
    if (!userProfile) return null

    let filter = {}
    
    if (userProfile?.role === 'attendee') {
      filter = { filter: `attendee_id=eq.${userProfile?.id}` }
    } else if (userProfile?.role === 'host') {
      // Note: This requires a function or view to filter by host_id through events
      filter = { filter: `attendee_id=eq.${userProfile?.id}` } // Fallback to own bookings
    }
    // Admins see all bookings (no filter)

    const channel = supabase?.channel(`bookings-channel-${userProfile?.id}`)
    
    return channel?.on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'bookings',
        ...filter
      }, callback)?.subscribe();
  }
}

export default supabase